/**
 * THE LIVING PANTHEON 🏛️
 * ═══════════════════════════════════════════════════════════════
 * 
 * Visual display of ALL gods in the Pantheon.
 * Worker Gods are shown as active/hired.
 * Council Gods are shown as dormant until summoned.
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Sparkles, Zap, Users, Lock, ChevronRight } from 'lucide-react';
import { WORKER_GODS, COUNCIL_GODS } from './CouncilGods';
import SummoningModal from './SummoningModal';

const GodThrone = ({ god, isActive, isDormant, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative group p-4 rounded-xl transition-all duration-500 text-center
        ${isDormant 
          ? 'bg-gray-900/30 border border-gray-800/50 cursor-pointer hover:border-gray-700'
          : isActive
            ? `bg-gradient-to-br ${god.bgColor} border border-${god.borderColor} shadow-lg ${god.glowColor}`
            : `bg-gray-800/50 border border-gray-700 cursor-pointer hover:border-gray-600`
        }
      `}
      data-testid={`throne-${god.id}`}
    >
      {/* Dormant Mist Effect */}
      {isDormant && (
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent rounded-xl pointer-events-none" />
      )}
      
      {/* Glow Effect for Active Gods */}
      {isActive && (
        <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${god.color} opacity-10 blur-xl -z-10`} />
      )}
      
      {/* God Avatar */}
      <div className={`
        relative w-16 h-16 mx-auto rounded-full overflow-hidden flex items-center justify-center text-3xl mb-3
        ${isDormant 
          ? 'bg-gray-800 grayscale opacity-50' 
          : `bg-gradient-to-br ${god.color} shadow-lg`
        }
        ${isHovered && !isDormant ? 'scale-110' : ''}
        transition-transform duration-300
      `}>
        {god.portrait ? (
          <img src={god.portrait} alt={god.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          god.emoji
        )}
        
        {/* Active Indicator */}
        {isActive && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-gray-900 flex items-center justify-center">
            <Zap className="w-3 h-3 text-white" />
          </div>
        )}
        
        {/* Dormant Lock */}
        {isDormant && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gray-700 rounded-full border-2 border-gray-900 flex items-center justify-center">
            <Lock className="w-3 h-3 text-gray-500" />
          </div>
        )}
      </div>
      
      {/* God Name */}
      <h4 className={`font-bold text-sm ${isDormant ? 'text-gray-600' : god.textColor}`}>
        {god.name}
      </h4>
      
      {/* God Domain */}
      <p className={`text-xs ${isDormant ? 'text-gray-700' : 'text-gray-500'} truncate`}>
        {god.domain}
      </p>
      
      {/* Summon Hint on Hover (for dormant) */}
      {isDormant && isHovered && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/90 rounded-xl">
          <div className="text-center">
            <Sparkles className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <span className="text-xs text-purple-300">Click to Summon</span>
          </div>
        </div>
      )}
    </button>
  );
};

const LivingPantheon = ({ 
  activeWorkerGods = ['athena', 'hermes', 'apollo', 'dionysus', 'ares'],
  summonedCouncilGods = [],
  onSummonCouncil,
  userCredits = 50,
  isPro = false,
  compact = false
}) => {
  const [showSummoningModal, setShowSummoningModal] = useState(false);
  
  const workerGods = Object.values(WORKER_GODS);
  const councilGods = Object.values(COUNCIL_GODS);
  
  const handleGodClick = (god) => {
    if (god.role === 'council') {
      setShowSummoningModal(true);
    }
  };
  
  const handleSummon = (selectedGods) => {
    onSummonCouncil?.(selectedGods);
  };
  
  if (compact) {
    // Compact view for sidebar/header
    return (
      <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-400" />
            The Pantheon
          </h3>
          <span className="text-xs text-gray-500">
            {activeWorkerGods.length + summonedCouncilGods.length}/12 Active
          </span>
        </div>
        
        {/* Compact God Row */}
        <div className="flex items-center gap-1 flex-wrap">
          {/* Worker Gods */}
          {workerGods.map(god => (
            <div
              key={god.id}
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm overflow-hidden
                ${activeWorkerGods.includes(god.id)
                  ? `bg-gradient-to-br ${god.color}`
                  : 'bg-gray-800 opacity-30'
                }
              `}
              title={`${god.name} - ${activeWorkerGods.includes(god.id) ? 'Active' : 'Not Hired'}`}
            >
              {god.portrait ? (
                <img src={god.portrait} alt={god.name} className="w-full h-full object-cover" loading="lazy" />
              ) : god.emoji}
            </div>
          ))}
          
          {/* Divider */}
          <div className="w-px h-6 bg-gray-700 mx-1" />
          
          {/* Council Gods */}
          {councilGods.map(god => (
            <button
              key={god.id}
              onClick={() => setShowSummoningModal(true)}
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all overflow-hidden
                ${summonedCouncilGods.includes(god.id)
                  ? `bg-gradient-to-br ${god.color}`
                  : 'bg-gray-800/50 grayscale opacity-30 hover:opacity-50'
                }
              `}
              title={`${god.name} - ${summonedCouncilGods.includes(god.id) ? 'Summoned' : 'Dormant (Click to Summon)'}`}
            >
              {god.portrait ? (
                <img src={god.portrait} alt={god.name} className="w-full h-full object-cover" loading="lazy" />
              ) : god.emoji}
            </button>
          ))}
        </div>
        
        {/* Summon Button */}
        <button
          onClick={() => setShowSummoningModal(true)}
          className="w-full mt-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-400 text-xs font-medium flex items-center justify-center gap-2 transition-all"
        >
          <Sparkles className="w-3 h-3" />
          Summon Council Gods
          <ChevronRight className="w-3 h-3" />
        </button>
        
        {/* Summoning Modal */}
        <SummoningModal
          isOpen={showSummoningModal}
          onClose={() => setShowSummoningModal(false)}
          onSummon={handleSummon}
          activeWorkerGods={activeWorkerGods}
          userCredits={userCredits}
          isPro={isPro}
        />
      </div>
    );
  }
  
  // Full view
  return (
    <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">The Living Pantheon</h2>
            <p className="text-xs text-gray-500">
              {activeWorkerGods.length + summonedCouncilGods.length} of 12 gods active
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setShowSummoningModal(true)}
          className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-400 text-sm font-medium flex items-center gap-2 transition-all"
        >
          <Sparkles className="w-4 h-4" />
          Summon Gods
        </button>
      </div>
      
      {/* Worker Gods Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-green-400" />
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide">
            The Workers
          </h3>
          <span className="text-xs text-gray-600">Execute your campaigns</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {workerGods.map(god => (
            <GodThrone
              key={god.id}
              god={god}
              isActive={activeWorkerGods.includes(god.id)}
              isDormant={false}
              onClick={() => {}}
            />
          ))}
        </div>
      </div>
      
      {/* Council Gods Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide">
            The Shadow Council
          </h3>
          <span className="text-xs text-gray-600">Advise & debate strategy</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {councilGods.map(god => (
            <GodThrone
              key={god.id}
              god={god}
              isActive={summonedCouncilGods.includes(god.id)}
              isDormant={!summonedCouncilGods.includes(god.id)}
              onClick={() => handleGodClick(god)}
            />
          ))}
        </div>
      </div>
      
      {/* Hint */}
      {summonedCouncilGods.length === 0 && (
        <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl text-center">
          <p className="text-sm text-purple-300">
            <Sparkles className="w-4 h-4 inline mr-1" />
            Summon Council Gods to unlock strategic debates and diverse perspectives
          </p>
        </div>
      )}
      
      {/* Summoning Modal */}
      <SummoningModal
        isOpen={showSummoningModal}
        onClose={() => setShowSummoningModal(false)}
        onSummon={handleSummon}
        activeWorkerGods={activeWorkerGods}
        userCredits={userCredits}
        isPro={isPro}
      />
    </div>
  );
};

export default LivingPantheon;
